import { useCallback, useMemo } from 'react';
import { useSitesStore } from '../store';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useEntityCollectionInitializer } from 'store/entityCollection';
import { FormattedMessage } from 'intl';
import { translationKeyToString } from 'intl/types/translationKey';
import { DialogTranslationKeys } from 'intl/keys';

import { LabelledEntity } from 'shared/types/commonView';
import type { SiteModel } from 'shared/domain/site/types/model';
import { CommonTranslationKeys } from 'intl/keys';

export function useSitesInitializer(): void {
  useEntityCollectionInitializer(useSitesStore.use.actions());
}

export function useSites(): {
  items: SiteModel[];
  loading: boolean;
  /** True once a complete list arrived (also an empty one). */
  isReady: boolean;
  deleteSite: (site: LabelledEntity) => void;
  restoreSite: (site: LabelledEntity) => void;
} {
  const data = useSitesStore.use.data();
  const isReady = useSitesStore.use.isReady();
  const { archive, restore } = useSitesStore.use.actions();
  const createDialog = useDialog();

  const deleteSite = useCallback(
    (site: LabelledEntity) => {
      createDialog({
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteSiteTitle} />
        ),
        description: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteSiteDescription}
            values={{ siteName: site.label }}
          />
        ),
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.archive),
        ],
      }).then(() => archive(site._id));
    },
    [createDialog, archive]
  );

  const restoreSite = useCallback((site: LabelledEntity) => restore(site._id), [restore]);

  return useMemo(
    () => ({ items: data.items, loading: data.loading, isReady, deleteSite, restoreSite }),
    [data.items, data.loading, isReady, deleteSite, restoreSite]
  );
}
